import { CloseOutlined } from '@ant-design/icons';
import { Divider, Switch, Tag } from 'antd';
import { useMemo } from 'react';
import type { SimulationFilterEditedAction } from 'types/ppaSimulationMeta';
import type { SimulatorAppliedFilters, SimulatorFilterCategory } from './SimulatorFilterPanel';
import {
    buildSimulatorFilterDetailGroups,
    type SimulatorFilterDetailGroup,
} from './simulatorFilter.helpers';
import {
    StyledAppliedFiltersDetailPanel,
    StyledAppliedFiltersDetailPanelActions,
    StyledAppliedFiltersDetailPanelCancelButton,
    StyledAppliedFiltersDetailPanelApplyButton,
} from './SimulatorAppliedFiltersDetailPanel.styles';

type Props = {
    filterCategories: SimulatorFilterCategory[];
    editedActionTypes?: SimulationFilterEditedAction[];
    draftFilters: SimulatorAppliedFilters;
    onDraftChange: (filters: SimulatorAppliedFilters) => void;
    onClose: () => void;
    onCancel: () => void;
    onApply: () => void;
};

const removeCategorySelection = (
    filters: SimulatorAppliedFilters,
    categoryKey: string,
    optionId: string,
): SimulatorAppliedFilters => {
    const nextSelections = { ...filters.categorySelections };
    const currentIds = nextSelections[categoryKey] ?? [];
    const nextIds = currentIds.filter((id) => id !== optionId);

    if (nextIds.length === 0) {
        delete nextSelections[categoryKey];
    } else {
        nextSelections[categoryKey] = nextIds;
    }

    return {
        ...filters,
        categorySelections: nextSelections,
    };
};

const SimulatorAppliedFiltersDetailPanel = ({
    filterCategories,
    editedActionTypes = [],
    draftFilters,
    onDraftChange,
    onClose,
    onCancel,
    onApply,
}: Props) => {
    const detailGroups = useMemo(
        () =>
            buildSimulatorFilterDetailGroups(
                filterCategories,
                draftFilters.categorySelections,
                draftFilters.showOnly,
                editedActionTypes,
                { includeShowOnly: false },
            ),
        [draftFilters.categorySelections, draftFilters.showOnly, editedActionTypes, filterCategories],
    );

    const handleRemoveTag = (group: SimulatorFilterDetailGroup, optionId: string) => {
        onDraftChange(removeCategorySelection(draftFilters, group.key, optionId));
    };

    const handleEditedToggle = (checked: boolean) => {
        onDraftChange({
            ...draftFilters,
            showOnly: {
                edited: checked,
                impacted: checked ? draftFilters.showOnly.impacted : false,
                editedFilters: checked ? draftFilters.showOnly.editedFilters : [],
            },
        });
    };

    const handleImpactedToggle = (checked: boolean) => {
        onDraftChange({
            ...draftFilters,
            showOnly: {
                edited: checked ? true : draftFilters.showOnly.edited,
                impacted: checked,
                editedFilters: draftFilters.showOnly.editedFilters,
            },
        });
    };

    return (
        <StyledAppliedFiltersDetailPanel>
            <div className="applied-filters-detail-header">
                <div className="applied-filters-detail-header-left">
                    <p className="applied-filters-detail-title">Applied Filters</p>
                    <div className="applied-filters-detail-toggles">
                        {draftFilters.showOnly.edited ? (
                            <label className="applied-filters-detail-toggle">
                                <Switch
                                    size="small"
                                    checked={draftFilters.showOnly.edited}
                                    onChange={handleEditedToggle}
                                />
                                <span className="applied-filters-detail-toggle-label">Edited</span>
                            </label>
                        ) : null}
                        {draftFilters.showOnly.impacted ? (
                            <label className="applied-filters-detail-toggle">
                                <Switch
                                    size="small"
                                    checked={draftFilters.showOnly.impacted}
                                    onChange={handleImpactedToggle}
                                />
                                <span className="applied-filters-detail-toggle-label">Impacted</span>
                            </label>
                        ) : null}
                    </div>
                </div>
                <button
                    type="button"
                    className="applied-filters-detail-close"
                    aria-label="Close applied filters panel"
                    onClick={onClose}
                >
                    <CloseOutlined />
                </button>
            </div>

            <Divider className="applied-filters-detail-divider" />

            <div className="applied-filters-detail-rows">
                {detailGroups.length > 0 ? (
                    detailGroups.map((group) => (
                        <div key={group.key} className="applied-filters-detail-row">
                            <span className="applied-filters-detail-row-label">{group.label}:</span>
                            <div className="applied-filters-detail-tags">
                                {group.items.map((item) => (
                                    <Tag
                                        key={`${group.key}-${item.id}`}
                                        closable
                                        className="applied-filters-detail-tag"
                                        onClose={(event) => {
                                            event.preventDefault();
                                            handleRemoveTag(group, item.id);
                                        }}
                                    >
                                        {item.label}
                                    </Tag>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <span className="applied-filters-detail-empty">No selections</span>
                )}
            </div>

            <StyledAppliedFiltersDetailPanelActions>
                <StyledAppliedFiltersDetailPanelCancelButton onClick={onCancel}>
                    Cancel
                </StyledAppliedFiltersDetailPanelCancelButton>
                <StyledAppliedFiltersDetailPanelApplyButton type="primary" onClick={onApply}>
                    Apply
                </StyledAppliedFiltersDetailPanelApplyButton>
            </StyledAppliedFiltersDetailPanelActions>
        </StyledAppliedFiltersDetailPanel>
    );
};

export default SimulatorAppliedFiltersDetailPanel;
